import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Loader2, Check, X, Trash2, ShieldAlert, ShieldCheck, Filter } from "lucide-react";

const STATUS_OPTIONS = [
    { id: "pending", label: "Pending moderation" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "all", label: "All" },
];

const FLAG_LABELS = {
    private_case_details: "Private case details",
    private_pii: "Personal info",
    threat_language: "Threat / harassment",
    contains_url: "Contains URL",
    spam_keywords: "Spam keywords",
    unverified_serious_claim: "Unverified serious claim",
    all_caps: "All caps",
    very_short_negative: "Suspiciously short negative",
};

export default function AdminReviewsTab() {
    const [status, setStatus] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/reviews?status_filter=${status}&limit=200`);
            setItems(data || []);
        } catch {
            toast.error("Couldn't load reviews.");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [status]); // eslint-disable-line

    const moderate = async (id, action) => {
        setBusy(id);
        try {
            await api.post(`/admin/reviews/${id}/moderate`, { action });
            toast.success(action === "approve" ? "Review approved." : "Review rejected.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed.");
        } finally { setBusy(null); }
    };

    const remove = async (id) => {
        if (!window.confirm("Permanently delete this review?")) return;
        setBusy(id);
        try {
            await api.delete(`/admin/reviews/${id}`);
            toast.success("Review deleted.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-reviews-tab">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h2 className="font-serif text-2xl font-semibold">Attorney reviews moderation</h2>
                    <p className="text-xs text-muted-foreground mt-1">All client reviews are held here until you approve them. Flagged reviews are auto-tagged for triage.</p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-2 py-1">
                    <Filter className="h-3.5 w-3.5 text-muted-foreground ml-2" />
                    {STATUS_OPTIONS.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => setStatus(s.id)}
                            data-testid={`reviews-filter-${s.id}`}
                            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${status === s.id ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-accent"}`}>
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : items.length === 0 ? (
                <div className="ll-card text-sm text-muted-foreground text-center py-12" data-testid="reviews-empty">
                    No reviews to show in this filter.
                </div>
            ) : (
                <div className="space-y-3" data-testid="reviews-list">
                    {items.map((r) => (
                        <article key={r.id} className="ll-card" data-testid={`review-item-${r.id}`}>
                            <header className="flex items-center justify-between flex-wrap gap-2 mb-2">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium text-sm">{r.reviewer_name}</span>
                                    <span className="text-xs text-muted-foreground">· for {r.attorney_name || r.attorney_id}</span>
                                    {r.is_verified_contact && (
                                        <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-primary/10 text-primary border border-primary/30 px-2 py-0.5">
                                            <ShieldCheck className="h-3 w-3" /> Verified contact
                                        </span>
                                    )}
                                    <span className={`text-[11px] rounded-full px-2 py-0.5 ${r.status === "approved" ? "bg-green-500/10 text-green-700 border border-green-500/30" : r.status === "rejected" ? "bg-destructive/10 text-destructive border border-destructive/30" : "bg-amber-500/10 text-amber-700 border border-amber-500/30"}`} data-testid={`review-status-${r.id}`}>
                                        {r.status}
                                    </span>
                                </div>
                                <div className="text-amber-500 text-sm font-semibold tabular-nums">
                                    {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                                </div>
                            </header>

                            {(r.flag_reasons || []).length > 0 && (
                                <div className="mb-2 flex flex-wrap gap-1" data-testid={`review-flags-${r.id}`}>
                                    {r.flag_reasons.map((f) => (
                                        <span key={f} className="inline-flex items-center gap-1 text-[11px] rounded-full bg-destructive/10 text-destructive border border-destructive/30 px-2 py-0.5">
                                            <ShieldAlert className="h-3 w-3" /> {FLAG_LABELS[f] || f}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <p className="text-sm whitespace-pre-wrap mb-3">{r.comment}</p>

                            <div className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
                                <span>From: {r.reviewer_email}</span>
                                <span>· Submitted {new Date(r.created_at).toLocaleString()}</span>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 mt-3">
                                {r.status !== "approved" && (
                                    <button
                                        type="button"
                                        onClick={() => moderate(r.id, "approve")}
                                        disabled={busy === r.id}
                                        className="inline-flex items-center gap-1.5 text-xs rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-semibold hover:bg-primary/90 disabled:opacity-60"
                                        data-testid={`review-approve-${r.id}`}>
                                        <Check className="h-3.5 w-3.5" /> Approve
                                    </button>
                                )}
                                {r.status !== "rejected" && (
                                    <button
                                        type="button"
                                        onClick={() => moderate(r.id, "reject")}
                                        disabled={busy === r.id}
                                        className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1.5 font-semibold hover:bg-accent disabled:opacity-60"
                                        data-testid={`review-reject-${r.id}`}>
                                        <X className="h-3.5 w-3.5" /> Reject
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => remove(r.id)}
                                    disabled={busy === r.id}
                                    className="ml-auto inline-flex items-center gap-1.5 text-xs rounded-full border border-destructive/40 text-destructive px-3 py-1.5 font-semibold hover:bg-destructive/10 disabled:opacity-60"
                                    data-testid={`review-delete-${r.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}
